/**
 * Protokół multiplayer Park (Colyseus) — zamrożony w WP0.
 *
 * R38: ZERO pól wolnego tekstu między graczami.
 * Jedyny publiczny string to dogName (moderowany przy zapisie).
 *
 * ─── PRESENCE / RECONNECT (PLAN_REVIEW2 korekta 2) ───────────────────
 *
 * Single-active-session: serwer trzyma mapę
 *   playerId → { roomId, sessionId }
 *
 * Semantyka cleanup = **compare-and-delete** po krotce
 *   (playerId, roomId, sessionId):
 *
 *   presence.deleteIfMatch(playerId, roomId, sessionId)
 *     → usuwa wpis TYLKO gdy wszystkie 3 pola się zgadzają
 *     → jeśli mapa ma już innego sessionId (następca wygrał),
 *       cleanup starego klienta jest NO-OP
 *
 * Ścieżka SESSION_TAKEN (kod leave 4001):
 *   1. onJoin nowej sesji widzi istniejącą mapę
 *   2. Mapa jest AKTUALIZOWANA na nowego sessionId (atomowo w ticku)
 *   3. Stary klient dostaje client.leave(4001 SESSION_TAKEN)
 *   4. onLeave starego klienta widzi consented/code 4001
 *      i woła cleanup z WŁASNYM (playerId, roomId, oldSessionId)
 *   5. compare-and-delete NIE usuwa mapowania następcy
 *      (sessionId nie pasuje)
 *
 * WP3 implementuje:
 *   - presence.ts: set / deleteIfMatch / get
 *   - ParkRoom.onLeave: cleanup WARUNKOWY przez deleteIfMatch
 *   - osobna ścieżka SESSION_TAKEN (bez usuwania następcy)
 *   - test: drugi join dokładnie podczas allowReconnection
 */

/** Kod Colyseus leave: sesja przejęta przez inne urządzenie. */
export const LEAVE_CODE_SESSION_TAKEN = 4001 as const;

export type PresenceEntry = {
  playerId: string;
  roomId: string;
  sessionId: string;
};

/**
 * Kontrakt operacji presence — WP3 implementuje, WP0 zamraża semantyka.
 *
 * deleteIfMatch: compare-and-delete po pełnej krotce.
 * Zwraca true gdy usunięto, false gdy wpis należał już do kogoś innego
 * (lub nie istniał) — ścieżka SESSION_TAKEN polega na false.
 */
export interface PresenceStore {
  set(entry: PresenceEntry): void;
  get(playerId: string): PresenceEntry | undefined;
  /**
   * Usuń wpis tylko gdy (playerId, roomId, sessionId) == stored.
   * NIE usuwa mapowania następcy.
   */
  deleteIfMatch(playerId: string, roomId: string, sessionId: string): boolean;
}

/** Powód leave — steruje cleanup w onLeave. */
export type LeaveReason =
  | { kind: 'consented' }
  | { kind: 'session_taken' } // 4001 — NIE czyść presence następcy
  | { kind: 'disconnect' } // allowReconnection; cleanup dopiero po timeout
  | { kind: 'reconnection_timeout' };

/**
 * Helper decyzyjny dla WP3 — czy onLeave powinien wołać presence.deleteIfMatch.
 * SESSION_TAKEN → tak (z własnym sessionId; deleteIfMatch ochroni następcę).
 * disconnect → nie (czekamy na allowReconnection).
 * consented / reconnection_timeout → tak.
 */
export function shouldCleanupPresenceOnLeave(reason: LeaveReason): boolean {
  switch (reason.kind) {
    case 'session_taken':
      return true; // deleteIfMatch z old sessionId = bezpieczny no-op jeśli następca wygrał
    case 'disconnect':
      return false;
    case 'consented':
    case 'reconnection_timeout':
      return true;
  }
}

// ─── Client → Server messages (brak wolnego tekstu) ───────────────────
export type ClientMessage =
  | { type: 'moveTo'; x: number; y: number }
  | { type: 'emote'; id: EmoteId }
  | { type: 'petNpc'; npcId: string }
  | { type: 'kickBall'; dirX: number; dirY: number };

// ─── Server → Client events ──────────────────────────────────────────
export type ServerEvent =
  | { type: 'npcHearts'; npcId: string }
  | { type: 'petSmiled'; targetId: string }
  | { type: 'sessionTaken' };

export const EMOTE_IDS = [
  'heart',
  'wave',
  'happy',
  'sleep',
  'star',
  'ball',
] as const;
export type EmoteId = (typeof EMOTE_IDS)[number];

export const ANIM_IDS = [
  'idle',
  'walk',
  'sit',
  'jump',
  'swim',
  'zzz',
] as const;
export type AnimId = (typeof ANIM_IDS)[number];

/** Snapshot gracza w ParkState — bez wolnego tekstu poza dogName. */
export type ParkPlayerPublic = {
  sessionId: string;
  playerId: string;
  dogName: string; // moderowane
  breed: string;
  color: string;
  x: number;
  y: number;
  tx: number;
  ty: number;
  anim: AnimId;
  emote: EmoteId | '';
  outfit: string; // JSON serializowany, katalog id
  connected: boolean;
};

export type ParkNpcPublic = {
  npcId: string;
  x: number;
  y: number;
  anim: AnimId;
  emote: EmoteId | '';
};

export type ParkBallPublic = {
  x: number;
  y: number;
  vx: number;
  vy: number;
};

export const DOG_SPEED = 180; // px/s
export const PARK_TICK_HZ = 20;
export const RECONNECT_WINDOW_SEC = 120;
export const MAX_CLIENTS_PER_PARK = 16;
